/**
 * PanelRadioTemplatesV1 — Éditeur de messages radio ANTARES
 * Table : game_radio_message_template
 * Accès : CM (rôle 1) + Référent Scénariste
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type Phase =
  | 'depart'
  | 'arrivee_statut'
  | 'ambiance_cos'
  | 'en_cours_1'
  | 'en_cours_2'
  | 'bilan_samu'
  | 'fin'
  | 'retour_statut';

type TemplateStatus = 'actif' | 'en_creation';

interface RadioTemplate {
  id: number;
  phase: Phase;
  message_template: string;
  statut_radio: number | null;
  service_id: number | null;
  incident_type_id: number | null;
  ordre: number;
  status: TemplateStatus;
  created_at?: string;
  updated_at?: string;
}

interface FormState {
  phase: Phase;
  message_template: string;
  statut_radio: string;
  service_id: string;
  ordre: string;
  status: TemplateStatus;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const PHASES: { value: Phase; label: string; description: string; icon: string }[] = [
  { value: 'depart',         label: 'Départ',          description: 'Véhicule quitte la caserne',         icon: '🚒' },
  { value: 'arrivee_statut', label: 'Arrivée',          description: 'Véhicule arrive sur les lieux',      icon: '📍' },
  { value: 'ambiance_cos',   label: 'Ambiance COS',     description: 'Message narratif du commandant',     icon: '🎙️' },
  { value: 'en_cours_1',     label: 'En cours (1)',     description: 'Situation en cours — premier point', icon: '📡' },
  { value: 'en_cours_2',     label: 'En cours (2)',     description: 'Situation en cours — point avancé',  icon: '📡' },
  { value: 'bilan_samu',     label: 'Bilan SAMU',       description: 'Transmission bilan victime au SAMU', icon: '🏥' },
  { value: 'fin',            label: 'Fin intervention', description: 'Levée de l\'intervention',           icon: '✅' },
  { value: 'retour_statut',  label: 'Retour dispo',     description: 'Véhicule retourne disponible',       icon: '🔄' },
];

const STATUTS_RADIO = [
  { value: '',  label: 'Aucun — message narratif' },
  { value: '2', label: 'Statut 2 — en route' },
  { value: '3', label: 'Statut 3 — sur place' },
  { value: '4', label: 'Statut 4 — quitte les lieux' },
  { value: '7', label: 'Statut 7 — disponible' },
];

const SERVICES = [
  { value: '',  label: 'Tous les services' },
  { value: '2', label: 'Sapeurs-pompiers (SP)' },
  { value: '3', label: 'SAMU / SMUR' },
  { value: '4', label: 'Forces de l\'ordre (FDO)' },
];

// Variables disponibles et leurs valeurs de prévisualisation
const VARIABLES: { tag: string; label: string; preview: string }[] = [
  { tag: '{num_inter}',        label: 'N° intervention',     preview: '42' },
  { tag: '{indicatif}',       label: 'Indicatif radio',     preview: 'VSAV-1-01' },
  { tag: '{grade}',           label: 'Grade du chef',       preview: 'SGT' },
  { tag: '{initiale_prenom}', label: 'Initiale prénom',     preview: 'J.' },
  { tag: '{nom}',             label: 'Nom du chef',         preview: 'DUPONT' },
  { tag: '{adresse}',         label: 'Adresse',             preview: '12 RUE DE LA PAIX' },
  { tag: '{dep}',             label: 'Département',         preview: '75' },
  { tag: '{type_incident}',   label: 'Type incident',       preview: 'ACCIDENT DE LA VOIE PUBLIQUE' },
  { tag: '{victimes}',        label: 'Nb victimes',         preview: '2' },
  { tag: '{heure}',           label: 'Heure',               preview: '14:32' },
];

const EMPTY_FORM: FormState = {
  phase:            'depart',
  message_template: '',
  statut_radio:     '',
  service_id:       '',
  ordre:            '0',
  status:           'actif',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function getServiceLabel(id: number | null): string {
  if (id === 2) return 'SP';
  if (id === 3) return 'SAMU';
  if (id === 4) return 'FDO';
  return 'Tous';
}

function renderPreview(template: string): string {
  let result = template;
  for (const v of VARIABLES) {
    result = result.split(v.tag).join(`[${v.preview}]`);
  }
  return result || '(message vide)';
}

// ─── Composant principal ───────────────────────────────────────────────────────

interface PanelRadioTemplatesV1Props {
  embedded?: boolean;
}

const PanelRadioTemplatesV1: React.FC<PanelRadioTemplatesV1Props> = () => {
  const [templates, setTemplates]       = useState<RadioTemplate[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [success, setSuccess]           = useState<string | null>(null);
  const [activePhase, setActivePhase]   = useState<Phase>('depart');
  const [editingId, setEditingId]       = useState<number | null>(null);
  const [showForm, setShowForm]         = useState(false);
  const [form, setForm]                 = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showPreview, setShowPreview]   = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formRef     = useRef<HTMLDivElement>(null);
  const api         = getApiBaseUrl();

  // ── Chargement ──────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r    = await fetch(`${api}/api/panel/v1/radio-templates?limit=500`, { headers: authHeaders() });
      const json = await r.json();
      if (!json.success) throw new Error(json.error || 'Erreur chargement');
      setTemplates(json.data || []);
    } catch (e: any) {
      setError(e?.message || 'Erreur réseau');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  // Effacer les messages de succès après 3s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(t);
  }, [success]);

  // ── Formulaire ──────────────────────────────────────────────────────────────

  function openCreate(phase?: Phase) {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, phase: phase ?? activePhase });
    setShowForm(true);
    setShowPreview(false);
    setError(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function openEdit(t: RadioTemplate) {
    setEditingId(t.id);
    setForm({
      phase:            t.phase,
      message_template: t.message_template,
      statut_radio:     t.statut_radio != null ? String(t.statut_radio) : '',
      service_id:       t.service_id   != null ? String(t.service_id)   : '',
      ordre:            String(t.ordre),
      status:           t.status,
    });
    setShowForm(true);
    setShowPreview(false);
    setError(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function closeForm() {
    setEditingId(null);
    setShowForm(false);
    setForm(EMPTY_FORM);
    setShowPreview(false);
  }

  // Insérer une variable à la position du curseur dans le textarea
  function insertVariable(tag: string) {
    const ta = textareaRef.current;
    if (!ta) {
      setForm((f) => ({ ...f, message_template: f.message_template + tag }));
      return;
    }
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    const before = form.message_template.slice(0, start);
    const after  = form.message_template.slice(end);
    const next   = before + tag + after;
    setForm((f) => ({ ...f, message_template: next }));
    setTimeout(() => {
      ta.focus();
      const pos = start + tag.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }

  async function handleSave() {
    if (!form.message_template.trim()) {
      setError('Le message est obligatoire.');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      phase:            form.phase,
      message_template: form.message_template.trim(),
      statut_radio:     form.statut_radio !== '' ? parseInt(form.statut_radio, 10) : null,
      service_id:       form.service_id   !== '' ? parseInt(form.service_id, 10)   : null,
      ordre:            parseInt(form.ordre, 10) || 0,
      status:           form.status,
    };
    try {
      const url    = editingId !== null ? `${api}/api/panel/v1/radio-templates/${editingId}` : `${api}/api/panel/v1/radio-templates`;
      const method = editingId !== null ? 'PUT' : 'POST';
      const r      = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const json   = await r.json();
      if (!json.success) throw new Error(json.error || 'Erreur sauvegarde');
      setSuccess(editingId !== null ? 'Message mis à jour.' : 'Message créé.');
      setActivePhase(form.phase);
      closeForm();
      void load();
    } catch (e: any) {
      setError(e?.message || 'Erreur réseau');
    }
    setSaving(false);
  }

  async function handleDelete(id: number) {
    try {
      const r    = await fetch(`${api}/api/panel/v1/radio-templates/${id}`, { method: 'DELETE', headers: authHeaders() });
      const json = await r.json();
      if (!json.success) throw new Error(json.error || 'Erreur suppression');
      setSuccess('Message supprimé.');
      setDeleteConfirm(null);
      void load();
    } catch (e: any) {
      setError(e?.message || 'Erreur réseau');
    }
  }

  async function toggleStatus(t: RadioTemplate) {
    const next = t.status === 'actif' ? 'en_creation' : 'actif';
    try {
      const r    = await fetch(`${api}/api/panel/v1/radio-templates/${t.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: next }),
      });
      const json = await r.json();
      if (!json.success) throw new Error(json.error || 'Erreur');
      setSuccess(next === 'actif' ? 'Message activé.' : 'Message désactivé.');
      void load();
    } catch (e: any) {
      setError(e?.message || 'Erreur réseau');
    }
  }

  // ── Données filtrées ─────────────────────────────────────────────────────────

  const phaseTemplates = templates.filter((t) => t.phase === activePhase);
  const activeCount    = templates.filter((t) => t.status === 'actif').length;

  // ── Rendu ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 pb-12">

      {/* En-tête */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-white">Messages radio ANTARES</h1>
          <p className="text-xs text-slate-400 mt-1 max-w-xl">
            Ces messages sont envoyés automatiquement lors des interventions.
            Chaque phase peut avoir plusieurs variantes — le système en choisit une aléatoirement.
            {' '}<span className="text-emerald-400">{activeCount} message{activeCount !== 1 ? 's' : ''} actif{activeCount !== 1 ? 's' : ''}</span> au total.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openCreate()}
          className="flex items-center gap-2 rounded-lg border border-[#3d4fdb]/50 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nouveau message
        </button>
      </div>

      {/* Alertes */}
      {error   && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">
          <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-200">
          <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {success}
        </div>
      )}

      {/* Onglets par phase */}
      <div className="flex gap-1 flex-wrap">
        {PHASES.map((p) => {
          const count  = templates.filter((t) => t.phase === p.value).length;
          const active = activePhase === p.value;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => setActivePhase(p.value)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                active
                  ? 'bg-[#3d4fdb]/30 border border-[#3d4fdb]/60 text-[#8ea0f5]'
                  : 'border border-[#243052] bg-[#0d152b]/60 text-slate-400 hover:text-slate-200 hover:border-[#2a3a62]'
              }`}
            >
              <span>{p.icon}</span>
              <span>{p.label}</span>
              {count > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                  active ? 'bg-[#3d4fdb]/50 text-[#c5ccff]' : 'bg-slate-700 text-slate-400'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Description de la phase active */}
      {(() => {
        const p = PHASES.find((x) => x.value === activePhase);
        return p ? (
          <div className="flex items-center justify-between rounded-lg border border-[#1e2d4d] bg-[#0a132b]/60 px-4 py-3">
            <div>
              <span className="text-sm font-semibold text-slate-200">{p.icon} {p.label}</span>
              <span className="text-slate-500 text-xs ml-2">— {p.description}</span>
            </div>
            <button
              type="button"
              onClick={() => openCreate(activePhase)}
              className="text-xs text-[#8ea0f5] hover:text-white underline underline-offset-2 transition-colors"
            >
              + Ajouter un message pour cette phase
            </button>
          </div>
        ) : null;
      })()}

      {/* Liste des templates de la phase */}
      {loading ? (
        <div className="flex items-center gap-3 py-8 text-slate-500 text-sm">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Chargement…
        </div>
      ) : phaseTemplates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#243052] bg-[#0d152b]/40 px-6 py-10 text-center">
          <p className="text-slate-400 text-sm">Aucun message pour cette phase.</p>
          <button
            type="button"
            onClick={() => openCreate(activePhase)}
            className="mt-3 text-xs text-[#8ea0f5] hover:text-white underline underline-offset-2"
          >
            Créer le premier message
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {phaseTemplates
            .sort((a, b) => a.ordre - b.ordre)
            .map((t) => (
            <div
              key={t.id}
              className={`rounded-xl border transition-colors ${
                t.status === 'actif'
                  ? 'border-[#243052] bg-[#0d152b]/90'
                  : 'border-[#1a2240] bg-[#080f1e]/60 opacity-70'
              }`}
            >
              {/* Carte header */}
              <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  {/* Badge statut */}
                  <button
                    type="button"
                    onClick={() => void toggleStatus(t)}
                    title={t.status === 'actif' ? 'Cliquer pour désactiver' : 'Cliquer pour activer'}
                    className={`shrink-0 flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold border transition-colors cursor-pointer ${
                      t.status === 'actif'
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25'
                        : 'bg-slate-700/40 text-slate-400 border-slate-600/40 hover:bg-slate-600/40'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${t.status === 'actif' ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                    {t.status === 'actif' ? 'Actif' : 'Inactif'}
                  </button>

                  {/* Service */}
                  {t.service_id && (
                    <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">
                      {getServiceLabel(t.service_id)}
                    </span>
                  )}

                  {/* Statut radio */}
                  {t.statut_radio !== null && (
                    <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                      Statut {t.statut_radio}
                    </span>
                  )}

                  {/* Ordre */}
                  <span className="text-[10px] text-slate-600">#{t.id} · ordre {t.ordre}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(t)}
                    className="rounded border border-[#2a3a62] bg-[#0a132b] px-2.5 py-1 text-[10px] font-medium text-slate-300 hover:text-white hover:border-[#3d4fdb]/60 transition-colors"
                  >
                    Modifier
                  </button>
                  {deleteConfirm === t.id ? (
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-slate-400">Confirmer ?</span>
                      <button
                        type="button"
                        onClick={() => void handleDelete(t.id)}
                        className="rounded border border-red-500/50 bg-red-500/20 px-2 py-1 text-[10px] font-medium text-red-300 hover:bg-red-500/30 transition-colors"
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(null)}
                        className="rounded border border-white/10 bg-black/30 px-2 py-1 text-[10px] text-slate-300 hover:bg-black/45 transition-colors"
                      >
                        Non
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(t.id)}
                      className="rounded border border-red-500/30 bg-transparent px-2 py-1 text-[10px] text-red-400 hover:bg-red-500/15 transition-colors"
                    >
                      Supprimer
                    </button>
                  )}
                </div>
              </div>

              {/* Message brut */}
              <div className="px-5 pb-2">
                <p className="font-mono text-xs text-slate-300 leading-relaxed">
                  {t.message_template}
                </p>
              </div>

              {/* Prévisualisation */}
              <div className="mx-5 mb-4 mt-1 rounded-lg bg-black/30 border border-[#1a2240] px-3 py-2">
                <p className="text-[10px] text-slate-600 mb-1 uppercase tracking-wide">Aperçu avec données réelles</p>
                <p className="text-xs text-slate-400 italic leading-relaxed">
                  {renderPreview(t.message_template)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulaire d'édition / création */}
      {showForm && (
        <div ref={formRef} className="rounded-xl border border-[#3d4fdb]/40 bg-[#080e20] p-6 space-y-5 shadow-xl shadow-black/40">
          {/* En-tête formulaire */}
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-100">
              {editingId !== null ? `Modifier le message #${editingId}` : 'Créer un nouveau message radio'}
            </h2>
            <button
              type="button"
              onClick={closeForm}
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Champs principaux */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Phase */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                Phase <span className="text-red-400">*</span>
              </label>
              <select
                value={form.phase}
                onChange={(e) => setForm((f) => ({ ...f, phase: e.target.value as Phase }))}
                className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
              >
                {PHASES.map((p) => (
                  <option key={p.value} value={p.value}>{p.icon} {p.label}</option>
                ))}
              </select>
            </div>

            {/* Statut radio */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                Statut radio
                <span className="ml-1 text-slate-600 font-normal">(optionnel)</span>
              </label>
              <select
                value={form.statut_radio}
                onChange={(e) => setForm((f) => ({ ...f, statut_radio: e.target.value }))}
                className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
              >
                {STATUTS_RADIO.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Service */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                Service concerné
                <span className="ml-1 text-slate-600 font-normal">(optionnel)</span>
              </label>
              <select
                value={form.service_id}
                onChange={(e) => setForm((f) => ({ ...f, service_id: e.target.value }))}
                className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
              >
                {SERVICES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Zone de message */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-slate-400">
                Message <span className="text-red-400">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="text-[10px] text-slate-500 hover:text-slate-300 underline underline-offset-2 transition-colors"
              >
                {showPreview ? 'Masquer l\'aperçu' : 'Voir l\'aperçu'}
              </button>
            </div>

            {/* Boutons d'insertion de variables */}
            <div className="mb-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] text-slate-600 self-center">Insérer :</span>
              {VARIABLES.map((v) => (
                <button
                  key={v.tag}
                  type="button"
                  onClick={() => insertVariable(v.tag)}
                  title={`Exemple : ${v.preview}`}
                  className="rounded border border-[#2a3a62] bg-[#0d1528] px-2 py-0.5 text-[10px] text-slate-300 hover:border-[#3d4fdb]/60 hover:text-[#8ea0f5] transition-colors font-mono"
                >
                  {v.tag}
                </button>
              ))}
            </div>

            <textarea
              ref={textareaRef}
              rows={4}
              value={form.message_template}
              onChange={(e) => setForm((f) => ({ ...f, message_template: e.target.value }))}
              placeholder="Ex : CODIS {dep}, {indicatif}, STATUT 3 — {adresse}, FIN"
              className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-[#3d4fdb]/60 font-mono resize-y"
            />

            {/* Légende des variables */}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5">
              {VARIABLES.map((v) => (
                <span key={v.tag} className="text-[10px] text-slate-600">
                  <span className="font-mono text-slate-500">{v.tag}</span> = {v.label}
                </span>
              ))}
            </div>
          </div>

          {/* Aperçu en direct */}
          {showPreview && (
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
              <p className="text-[10px] text-emerald-500/70 uppercase tracking-wide mb-1.5">Aperçu du message envoyé</p>
              <p className="text-sm text-emerald-200 leading-relaxed font-mono">
                {renderPreview(form.message_template) || <span className="text-slate-500 italic">Écrivez votre message ci-dessus…</span>}
              </p>
            </div>
          )}

          {/* Options avancées */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-1 border-t border-[#1a2240]">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">
                Ordre d'affichage
                <span className="block text-[10px] text-slate-600 font-normal mt-0.5">Plus petit = priorité plus haute</span>
              </label>
              <input
                type="number"
                min={0}
                value={form.ordre}
                onChange={(e) => setForm((f) => ({ ...f, ordre: e.target.value }))}
                className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">
                Activer immédiatement ?
                <span className="block text-[10px] text-slate-600 font-normal mt-0.5">Un message inactif n'est jamais envoyé</span>
              </label>
              <div className="flex gap-2 mt-1">
                {(['actif', 'en_creation'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, status: s }))}
                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                      form.status === s
                        ? s === 'actif'
                          ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-300'
                          : 'border-slate-600 bg-slate-700/40 text-slate-300'
                        : 'border-[#2a3a62] bg-transparent text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {s === 'actif' ? '✓ Actif' : '⏸ Inactif'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || !form.message_template.trim()}
              className="rounded-lg border border-[#3d4fdb]/50 bg-[#3d4fdb]/25 px-5 py-2.5 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/35 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Enregistrement…' : editingId !== null ? 'Mettre à jour' : 'Créer le message'}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="rounded-lg border border-white/10 bg-black/30 px-5 py-2.5 text-sm font-medium text-slate-300 hover:bg-black/50 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelRadioTemplatesV1;
